// The rpc protocol shared by the local Worker room (1.0) and the future server room.
// Views travel as `view` messages carrying Uint8Array frames, NOT as rpc Binary packets:
// @esengine/rpc 1.1.4's RpcClient silently drops Binary packets. The msgpack codec carries typed arrays
// natively, so every transport uses it (createProtocolCodec).

import { ErrorCode, rpc } from '@esengine/rpc'
import { msgpack, type Codec } from '@esengine/rpc/codec'
import type { AiTier, CountryCode, GameModeKey } from '@taxwar/content'
import type { CommandEnvelope } from './commands'
import type { SimEvent } from './events'
import type { MatchSeed } from './ids'
import type { MatchSetupView, PlayerPrivateState, ViewFrame } from './views'

/** Who plays a seat: a person through a transport, or a bot the sim runs from that seat's own fog. */
export type SeatControl = { readonly kind: 'human' } | { readonly kind: 'ai'; readonly tier: AiTier }

export interface Belligerent {
  readonly country: CountryCode
  readonly control: SeatControl
}

export interface SkirmishSetup {
  /** The rules of the war (content GAME_MODES). */
  readonly mode: GameModeKey
  readonly seed: MatchSeed
  /**
   * One entry per seat; the index is the seat's PlayerId. The mode bounds the count; every country appears once; the
   * local room hosts exactly one human seat.
   */
  readonly belligerents: readonly Belligerent[]
}

/** Codes a room answers an api request with (the rpc ApiError code field); clients branch on them, never on messages. */
export const RoomErrorCode = {
  unknownApi: ErrorCode.NOT_FOUND,
  invalidRequest: ErrorCode.INVALID_REQUEST,
  sessionExists: 'SESSION_EXISTS',
  noSession: 'NO_SESSION',
  matchEnded: 'MATCH_ENDED',
} as const
export type RoomErrorCode = (typeof RoomErrorCode)[keyof typeof RoomErrorCode]

/**
 * The room's own failure (an uncaught error in the sim or host, docs/ARCHITECTURE.md §13), sent as its last message
 * before it stops. It travels as data so every transport reports a room crash the same way.
 */
export interface RoomFault {
  readonly name: string
  readonly message: string
  /** The room-side stack, for the client's crash report. */
  readonly stack: string
}

/**
 * The messages of every war room, local or online. Player privacy, rule 1 (ARCHITECTURE §15): room → client messages
 * carry seats (PlayerId) and game state only — never an account id, an IP address or a location. Online, a field added
 * here goes to every player in the war.
 */
const WAR_ROOM_MESSAGES = {
  /** client → room */
  command: rpc.msg<Omit<CommandEnvelope, 'player'>>(),
  /** room → client: synced state */
  view: rpc.msg<ViewFrame>(),
  /** room → client: private economy/ledger */
  private: rpc.msg<PlayerPrivateState>(),
  /** room → client: visibility-filtered events for the ticks since the last publish */
  events: rpc.msg<readonly SimEvent[]>(),
  /** room → client: the room failed and has stopped; nothing follows */
  fault: rpc.msg<RoomFault>(),
}

/** The local (single-player) room: the client creates the war and may pause it (GDD §7). */
export const taxwarProtocol = rpc.define({
  api: {
    /** Create the war from a setup and join it. */
    startSkirmish: rpc.api<SkirmishSetup, MatchSetupView>(),
    /** Single-player pause (D10). */
    pause: rpc.api<void, void>(),
    resume: rpc.api<void, void>(),
  },
  msg: WAR_ROOM_MESSAGES,
})

export type TaxwarProtocol = typeof taxwarProtocol

/** What an online seat learns when it joins its war. */
export interface OnlineJoin {
  readonly setup: MatchSetupView
  /** The last command seq the room has seen from this seat: a reconnecting client continues after it. */
  readonly lastSeq: number
}

/** One seat's connection as its room reports it — the connection only, never who the player is (rule 1 above). */
export type SeatPresence =
  /** Not connected yet (the war has not started). */
  | { readonly kind: 'absent' }
  | { readonly kind: 'connected' }
  /** Its connection dropped mid-war: the room surrenders for it when the reconnect grace runs out. */
  | { readonly kind: 'away'; readonly surrenderInSeconds: number }

/** The room's state as every seat sees it; sent on each change and once a second while a countdown runs. */
export type RoomPresence =
  /** Waiting for every seat to connect; the room closes without a war when the join window runs out. */
  | { readonly phase: 'gathering'; readonly seats: readonly SeatPresence[]; readonly abandonInSeconds: number }
  /** Every seat is here: the war starts after a short countdown. */
  | { readonly phase: 'starting'; readonly seats: readonly SeatPresence[]; readonly startsInSeconds: number }
  | { readonly phase: 'running'; readonly seats: readonly SeatPresence[] }

/** An online war room (ARCHITECTURE §15): the matchmaker created the war; a verified seat joins it. No pause (D10). */
export const onlineWarProtocol = rpc.define({
  api: {
    /** Seat this connection (its claim was verified by the edge) and answer with the war and where commands resume. */
    join: rpc.api<void, OnlineJoin>(),
  },
  msg: {
    ...WAR_ROOM_MESSAGES,
    /** room → client: who is connected and the room's countdowns */
    presence: rpc.msg<RoomPresence>(),
  },
})

export type OnlineWarProtocol = typeof onlineWarProtocol

/** The codec every transport must use (typed arrays must survive the trip). */
export function createProtocolCodec(): Codec {
  return msgpack()
}
